import { useTranslation } from "@/i18n";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Gamepad2,
  Wand2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { DemoViewer } from "./DemoViewer";

/**
 * Props for the TutorialContent component.
 */
interface TutorialContentProps {
  /** Whether this is showing tutorials or flows */
  contentType: "tutorials" | "flows";
}

/**
 * Demo item structure
 */
interface Demo {
  id: string;
  title: string;
}

/**
 * Shuffles an array using the Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Temporary demo list - using the same demo ID 5 times
const TUTORIAL_DEMOS: Demo[] = [
  { id: "cm82tgxu9000003jp1uth68k2", title: "Demo 1" },
  { id: "cm9ibrh4m000404la8elwb4ie", title: "Demo 2" },
  { id: "cm9ikqjff000004jvaazcatoe", title: "Demo 3" },
  { id: "cm82tewov00bn03leh7z7fz6x", title: "Demo 4" },
  { id: "cm82tf9oi00e203jxcf3p7kob", title: "Demo 5" },
  { id: "cmfxpjyrp000004jgb29pblco", title: "Demo 6" },
  { id: "cmfxtpmgg000004kt0imm3jv8", title: "Demo 7" },
  { id: "cma3tkomt000004la6lz8bt6t", title: "Demo 8" },
];

const FLOW_DEMOS: Demo[] = [{ id: "flow-1", title: "Flow Optimizer" }];

/**
 * Tutorial/Flow content area component that displays interactive demos.
 * Shows Navattic demos with navigation controls.
 * The order of demos is randomized each time the component is mounted.
 *
 * @param props - Component props
 * @returns Tutorial content area with demo viewer and navigation
 */
export default function TutorialContent({ contentType }: TutorialContentProps) {
  const { t } = useTranslation();
  const demos = useMemo(
    () =>
      shuffleArray(contentType === "tutorials" ? TUTORIAL_DEMOS : FLOW_DEMOS),
    [contentType]
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(demos.length - 1, prev + 1));
  };

  const isFirstDemo = currentIndex === 0;
  const isLastDemo = currentIndex === demos.length - 1;
  const currentDemo = demos[currentIndex];

  return (
    <div className="flex flex-col gap-3 items-center w-full min-w-[900px] bg-white rounded-2xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] p-4">
      {/* Header */}
      <div className="flex gap-3 items-center w-full">
        <div className="flex flex-1 gap-2 items-center">
          {contentType === "tutorials" ? (
            <Wand2 size={18} className="text-zinc-700" />
          ) : (
            <Gamepad2 size={18} className="text-zinc-700" />
          )}
          <p className="text-sm font-medium text-zinc-700">
            {contentType === "tutorials"
              ? t("documentUpload.tutorial.waitingText")
              : t("documentUpload.tutorial.videoTitle")}
          </p>
        </div>
        {contentType === "tutorials" && (
          <button
            onClick={() =>
              window.open(
                `https://capture.navattic.com/${currentDemo.id}`,
                "_blank"
              )
            }
            className="p-1.5 bg-transparent border-none cursor-pointer hover:bg-zinc-100 rounded-md transition-colors"
            title={t("documentUpload.tutorial.openInNewTab")}
          >
            <ExternalLink size={16} className="text-zinc-700" />
          </button>
        )}
      </div>

      {/* Content Area - Demo Viewer or Game */}
      <div className="w-full">
        {contentType === "tutorials" ? (
          <DemoViewer demoId={currentDemo.id} />
        ) : (
          <div className="relative w-full h-[600px] rounded-lg overflow-hidden bg-zinc-50">
            <iframe
              src="/game.html"
              className="absolute inset-0 w-full h-full border-none"
              title="Flow Optimizer Game"
            />
          </div>
        )}
      </div>

      {/* Footer Navigation - Only show for tutorials */}
      {contentType === "tutorials" && (
        <div className="flex gap-3 items-center w-full">
          <p className="text-xs text-zinc-400">
            {t("documentUpload.tutorial.demoCounter", {
              current: currentIndex + 1,
              total: demos.length,
            })}
          </p>
          <div className="flex-1 flex gap-1 items-center justify-center">
            {/* Progress indicators */}
            {demos.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentIndex
                    ? "w-12 bg-purple-600"
                    : "w-2 bg-zinc-300"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-3 items-center">
            <button
              onClick={handlePrevious}
              disabled={isFirstDemo}
              aria-label={t("documentUpload.tutorial.previousDemo")}
              title={t("documentUpload.tutorial.previousDemo")}
              className={`p-0 bg-transparent border-none transition-opacity ${
                isFirstDemo
                  ? "cursor-not-allowed opacity-40"
                  : "cursor-pointer hover:opacity-70"
              }`}
            >
              <ChevronLeft
                size={16}
                className={isFirstDemo ? "text-zinc-300" : "text-zinc-700"}
              />
            </button>
            <button
              onClick={handleNext}
              disabled={isLastDemo}
              aria-label={t("documentUpload.tutorial.nextDemo")}
              title={t("documentUpload.tutorial.nextDemo")}
              className={`p-0 bg-transparent border-none transition-opacity ${
                isLastDemo
                  ? "cursor-not-allowed opacity-40"
                  : "cursor-pointer hover:opacity-70"
              }`}
            >
              <ChevronRight
                size={16}
                className={isLastDemo ? "text-zinc-300" : "text-zinc-700"}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
